/**
 * Notification query options factory
 *
 * Centralizes query configuration for notification-related queries.
 * Used in __root.tsx layout and available for route loaders.
 */

import { queryOptions } from '@tanstack/react-query';
import type { ApiClient } from '@/lib/api';
import { retryTransientOnly } from '@/lib/query-retry';

export function notificationCountOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null
) {
  return queryOptions({
    queryKey: ['notifications', 'count', selectedOrgId] as const,
    queryFn: () => {
      if (!selectedOrgId) throw new Error('Organization not selected');
      return apiClient.notifications.getNotificationCount();
    },
    enabled: !!selectedOrgId,
    refetchInterval: () =>
      typeof document !== 'undefined' && document.visibilityState === 'visible'
        ? 60000
        : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    retry: retryTransientOnly,
  });
}

export function notificationRecentQueryKey(selectedOrgId: string | null) {
  return ['notifications', 'recent', selectedOrgId] as const;
}

export function notificationRecentOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null
) {
  return queryOptions({
    queryKey: notificationRecentQueryKey(selectedOrgId),
    queryFn: () => {
      if (!selectedOrgId) throw new Error('Organization not selected');
      return apiClient.notifications.getRecentNotifications();
    },
    enabled: !!selectedOrgId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
  });
}

export function notificationListQueryKey(
  selectedOrgId: string | null,
  params?: { limit?: number; offset?: number; status?: string; category?: string }
) {
  return ['notifications', 'list', selectedOrgId, params] as const;
}

export function notificationListOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  params?: { limit?: number; offset?: number; status?: string; category?: string }
) {
  return queryOptions({
    queryKey: notificationListQueryKey(selectedOrgId, params),
    queryFn: () => {
      if (!selectedOrgId) throw new Error('Organization not selected');
      return apiClient.notifications.getNotifications(params);
    },
    enabled: !!selectedOrgId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: retryTransientOnly,
  });
}
